import { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { Product } from 'queries';
import { Theme } from '@app/theme';
import { toEuros } from 'i18n';
import classNames from 'classnames';
import { SquareButton } from '@app/core';
import { getUnitProductPrice } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  product: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    minHeight: theme.height.tiny,
  },
  name: {
    ...theme.fonts.label,
  },
  price: {
    ...theme.fonts.label,
  },
  inherited: {
    opacity: 0.5,
  },
  buttons: {
    display: 'flex',
  },
  spacer: {
    flex: 1,
  },
}));

interface Props {
  product: Product;
  duration?: number;
  incentive?: number;
  inherited: boolean;

  removeProduct?: (productId: string) => any;
  sendUp?: (productId: string) => any;
  sendDown?: (productId: string) => any;
}

export const EditableProductLine = ({
  product,
  incentive,
  duration,
  inherited,
  removeProduct,
  sendUp,
  sendDown,
}: Props) => {
  const classes = useStyles();

  const productId = product.id;
  const onOverrideClick = useCallback(
    () => sendDown && sendDown(productId),
    [productId, sendDown],
  );
  const onSendUpClick = useCallback(
    () => sendUp && sendUp(productId),
    [productId, sendUp],
  );
  const onRemoveClick = useCallback(
    () => removeProduct && removeProduct(productId),
    [productId, removeProduct],
  );

  return (
    <div className={classes.product}>
      <div
        className={classNames(classes.name, {
          [classes.inherited]: inherited,
        })}
      >
        {product.title || product.name}
      </div>
      <div className={classes.spacer} />

      <div
        className={classNames(classes.price, {
          [classes.inherited]: inherited,
        })}
      >
        {toEuros(getUnitProductPrice(product, duration, incentive))}
      </div>
      <div className={classes.buttons}>
        {!!sendDown && (
          <SquareButton
            color="blue"
            icon="ri-arrow-down-circle-line"
            small
            onClick={onOverrideClick}
            variant="transparent"
            title="Surcharger"
          />
        )}
        {!!sendUp && (
          <SquareButton
            color="green"
            icon="ri-arrow-up-circle-line"
            small
            onClick={onSendUpClick}
            variant="transparent"
            title="Remonter"
          />
        )}
        {!!removeProduct && (
          <SquareButton
            color="red"
            icon="ri-delete-bin-line"
            small
            onClick={onRemoveClick}
            variant="transparent"
          />
        )}
      </div>
    </div>
  );
};
